import { ActorKind } from "@cabal/shared/domain/_common/enums.js";
import type { Product, ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import { TeamRole } from "@cabal/shared/domain/session/team/enums.js";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";

interface ToProductSummaryInput {
  product: Product;
  membersById: Record<string, Member>;
}

interface ProductOwnerInput {
  product: Product;
  membersById: Record<string, Member>;
}

interface ProductSummaryCollections {
  subproductIds: string[];
  versionIds: string[];
  collabIds: string[];
}

interface ProductSummaryCountSelection {
  collabCount: number;
  versionCount: number;
  subproductCount: number;
}

const ACTOR_KIND_BY_TEAM_ROLE: Record<TeamRole, ActorKind> = {
  [TeamRole.Chair]: ActorKind.Chair,
  [TeamRole.Watchdog]: ActorKind.Watchdog,
  [TeamRole.Envoy]: ActorKind.Envoy,
  [TeamRole.Operative]: ActorKind.Operative,
};

export function toMembersMap(items: readonly Member[]): Record<string, Member> {
  return items.reduce<Record<string, Member>>((acc, member) => {
    acc[member.id] = member;
    return acc;
  }, {});
}

export function toRecordById<T extends { id: string }>(
  items: readonly T[],
): Record<string, T> {
  return items.reduce<Record<string, T>>((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
}

export function toProductMapFromInitialLoad(input: {
  products: readonly Product[];
  membersById: Record<string, Member>;
}): Record<string, ProductSummary> {
  return input.products.reduce<Record<string, ProductSummary>>(
    (acc, product) => {
      acc[product.id] = toProductSummary({
        product,
        membersById: input.membersById,
      });
      return acc;
    },
    {},
  );
}

function toProductSummary({
  product,
  membersById,
}: ToProductSummaryInput): ProductSummary {
  const collections = selectProductSummaryCollections({ product });
  const counts = selectProductSummaryCounts({ collections });

  return {
    id: product.id,
    shortId: product.shortId,
    name: product.name,
    type: product.type,
    role: product.role,
    status: product.markedAs,
    parentProductId: product.parentProductId,
    subproductIds: collections.subproductIds,
    versionIds: collections.versionIds,
    owner: selectProductOwner({ product, membersById }),
    collabCount: counts.collabCount,
    unreadCollabCount: product.unreadCollabCount ?? 0,
    versionCount: counts.versionCount,
    subproductCount: counts.subproductCount,
    unseenSubproductCount: product.unseenSubproductCount ?? 0,
    hasUnreadContentVersion: product.hasUnreadContentVersion ?? false,
    latestVersionNumber: product.latestVersionNumber ?? null,
    hasEmptyLatestVersion: product.hasEmptyLatestVersion ?? false,
    roundFocusUntilStepstamp: product.roundFocusUntilStepstamp,
    isDeliverable: product.isDeliverable,
    isReadOnly: product.isReadOnly,
    selectedVersionId: product.selectedVersionId,
    contentMinLength: product.contentMinLength,
    contentMaxLength: product.contentMaxLength,
    createdAt: product.createdAt,
    updatedAt: product.updatedAt,
    attachedFiles: product.attachedFiles ?? [],
  };
}

function selectProductSummaryCollections(input: {
  product: Product;
}): ProductSummaryCollections {
  return {
    subproductIds: input.product.subproductIds ?? [],
    versionIds: input.product.versionIds ?? [],
    collabIds: input.product.collabIds ?? [],
  };
}

function selectProductSummaryCounts(input: {
  collections: ProductSummaryCollections;
}): ProductSummaryCountSelection {
  return {
    collabCount: input.collections.collabIds.length,
    versionCount: input.collections.versionIds.length,
    subproductCount: input.collections.subproductIds.length,
  };
}

function selectProductOwner({
  product,
  membersById,
}: ProductOwnerInput): ProductSummary["owner"] {
  if (!product.ownerMemberId) {
    return null;
  }

  const ownerMember = membersById[product.ownerMemberId];
  if (!ownerMember) {
    return null;
  }

  return {
    actorKind: ACTOR_KIND_BY_TEAM_ROLE[ownerMember.role],
    actorId: ownerMember.id,
    actorName: ownerMember.name,
  };
}
